import { Button } from '@eventbrite/eds-button';
import { ModalFooter, PaneLayoutInteractionType } from '@eventbrite/eds-modal';
import { GenericLazyString } from '@eventbrite/i18n';
import classNames from 'classnames';
import React, { FunctionComponent, ReactNode } from 'react';
import { getDisplayClasses } from '../../utils/displayClasses';
import ConnectedCartSummary from '../CartSummary';
import './PageFooter.scss';

export interface PageFooterProps {
    ctaText: GenericLazyString | string | null;
    ctaNode?: ReactNode;
    ctaType:
        | 'none'
        | 'link'
        | 'danger'
        | 'neutral'
        | 'fill'
        | 'inverse'
        | 'anchor'
        | 'follow'
        | 'apple_pay';
    footerMessageNode: ReactNode | undefined;
    handleSubmit: () => void;
    isSubmitting: boolean;
    isSubmitDisabled: boolean;
    isPaneOpen: boolean;
    keepDisabledStyleWhileLoading: boolean;
    modalFooterNode: ReactNode | undefined;
    onPaneCloseIntent?: () => void;
    onPaneToggle: () => void;
    paneInteraction: typeof PaneLayoutInteractionType;
    shouldAlignLeft: boolean;
    showCartSummaryButton: boolean;
    tertiaryNode: ReactNode | undefined;
    urgencySignalNode: ReactNode | undefined;
}

export const PageFooter: FunctionComponent<PageFooterProps> = ({
    ctaText,
    ctaNode,
    ctaType = 'fill',
    footerMessageNode,
    handleSubmit,
    isPaneOpen,
    isSubmitting,
    isSubmitDisabled,
    keepDisabledStyleWhileLoading,
    modalFooterNode,
    onPaneToggle,
    paneInteraction,
    shouldAlignLeft,
    showCartSummaryButton,
    tertiaryNode,
    urgencySignalNode,
}) => {
    // If there's nothing for modal footer just return
    if (!(modalFooterNode || ctaText || ctaNode || tertiaryNode)) return null;

    const parentBtnClass = classNames(
        shouldAlignLeft ? 'eds-align--left' : 'eds-align--right',
    );
    const className = classNames({
        'modal-footer-scrollable-on-desktop': shouldAlignLeft,
    });

    const defaultCtaButton = ctaText ? (
        <div className={parentBtnClass}>
            <Button
                onClick={handleSubmit}
                __containerClassName={className}
                style={ctaType}
                disabled={isSubmitDisabled}
                keepDisabledStyleWhileLoading={keepDisabledStyleWhileLoading}
                data-spec="eds-modal__primary-button"
                data-automation="eds-modal__primary-button"
                data-testid="eds-modal__primary-button"
                isLoading={isSubmitting}
                isFullWidth={true}
            >
                {ctaText}
            </Button>
        </div>
    ) : null;

    const actionNode = modalFooterNode || defaultCtaButton;

    const __footerContainerClassName = classNames(
        getDisplayClasses({ shouldAlignLeft }),
        'modal-footer',
        { 'modal-footer-cta-node': ctaNode },
        {
            'modal-footer--no-horizontal-pad modal-footer--no-left-margin modal-footer--no-top-border':
                shouldAlignLeft,
        },
    );

    const ctaNodeWrapperClassName = classNames({
        'cta-node__wrapper--align-right': !shouldAlignLeft,
    });

    const cartSummaryNode = showCartSummaryButton ? (
        <ConnectedCartSummary
            isPaneOpen={isPaneOpen}
            onClick={onPaneToggle}
            paneInteraction={paneInteraction}
        />
    ) : null;

    const ctaNodeWithWrapper = ctaNode ? (
        <div data-testid="cta-node-wrapper" className={ctaNodeWrapperClassName}>
            {ctaNode}
        </div>
    ) : null;

    const contentNode = tertiaryNode || (
        <>
            {urgencySignalNode}
            {footerMessageNode}
            {cartSummaryNode}
            {ctaNodeWithWrapper}
        </>
    );

    return (
        <ModalFooter __footerContainerClassName={__footerContainerClassName}>
            <div className="modal-footer__wrapper">
                {contentNode}
                {actionNode}
            </div>
        </ModalFooter>
    );
};
